/**
 * As casas da família, em tabela.
 *
 * Papel: mostra uma linha por residência, com o nome, o apelido, a cidade com a
 * UF e os botões de editar e remover.
 *
 * Como usar: quem monta a tabela passa `onEditar` e `onRemover` apenas para
 * quem tem nível `editar`; sem eles a coluna de ações some inteira, em vez de
 * oferecer botões que a API recusaria com 403.
 *
 * É tabela, e não grade de cartões: as colunas são curtas e quase sempre
 * preenchidas, e a comparação que interessa aqui — os lugares lado a lado — se
 * faz lendo a coluna para baixo.
 *
 * A coluna "fica dentro de" é o que registra que a Villa Prado está na Fazenda
 * Boa Vista. Quem não está dentro de nada mostra travessão, e não espaço em
 * branco: célula vazia numa tabela lida como dado que faltou preencher.
 *
 * `cidade` e `uf` aparecem juntas numa célula porque é assim que um endereço se
 * lê, mas continuam colunas separadas no banco: a composição é da renderização,
 * e não do dado.
 */
import { type Residencia } from '@concierge/shared';

import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

interface TabelaResidenciasProps {
  residencias: Residencia[];
  /** Chamado com a casa da linha; ausente esconde a coluna de ações. */
  onEditar?: (residencia: Residencia) => void;
  /** Chamado com a casa a excluir; a confirmação é de quem monta a tabela. */
  onRemover?: (residencia: Residencia) => void;
}

/**
 * A tabela de residências.
 *
 * @param residencias - As casas a mostrar, já em ordem.
 * @param onEditar - Chamado ao clicar em editar; ausente esconde as ações.
 * @param onRemover - Chamado ao clicar em remover; ausente esconde as ações.
 * @returns A tabela com uma linha por casa.
 */
export function TabelaResidencias({
  residencias,
  onEditar,
  onRemover,
}: TabelaResidenciasProps) {
  const mostrarAcoes = onEditar !== undefined || onRemover !== undefined;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nome</TableHead>
          <TableHead>Apelido</TableHead>
          <TableHead>Cidade</TableHead>
          <TableHead>Fica dentro de</TableHead>
          {mostrarAcoes && (
            <TableHead>
              <span className="sr-only">Ações</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {residencias.map((residencia) => (
          <TableRow key={residencia.id}>
            <TableCell className="text-texto-medio">{residencia.nome}</TableCell>
            <TableCell>{residencia.apelido}</TableCell>
            <TableCell>
              {residencia.cidade}, {residencia.uf}
            </TableCell>
            <TableCell>{residencia.dentroDe?.nome ?? '—'}</TableCell>
            {mostrarAcoes && (
              <TableCell>
                <div className="flex gap-2">
                  {onEditar !== undefined && (
                    <Button variant="outline" size="sm" onClick={() => onEditar(residencia)}>
                      editar
                    </Button>
                  )}
                  {onRemover !== undefined && (
                    <Button variant="destructive" onClick={() => onRemover(residencia)}>
                      remover
                    </Button>
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
